import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import Reveal from "@/components/Reveal";

const IMAGES = [
  { src: "/images/lieux/baie-abbaye-01.jpeg", fr: "La baie du Mont-Saint-Michel", en: "The bay of Mont-Saint-Michel", tall: true },
  { src: "/images/lieux/prieure-11.webp", fr: "Le Prieuré sous le grand ciel", en: "The Priory under the open sky", tall: true },
  { src: "/images/lieux/prieure-02.jpeg", fr: "La baie en fleurs", en: "The bay in bloom", tall: false },
  { src: "/images/lieux/prieure-07.webp", fr: "La cour d'honneur du Prieuré", en: "The Priory's main courtyard", tall: false },
  { src: "/images/lieux/lieu-04.jpeg", fr: "La grève à marée montante", en: "The shore at rising tide", tall: true },
  { src: "/images/lieux/prieure-09.webp", fr: "Le logis du Prieuré", en: "The Priory lodging house", tall: false },
  { src: "/images/lieux/interieur-81.webp", fr: "Le salon au piano à queue", en: "The grand piano salon", tall: false },
  { src: "/images/lieux/prieure-10.webp", fr: "Le puits et le jardin", en: "The well and the garden", tall: false },
  { src: "/images/lieux/interieur-76.webp", fr: "L'escalier de pierre", en: "The stone staircase", tall: true },
  { src: "/images/lieux/interieur-82.webp", fr: "La cheminée du salon", en: "The salon fireplace", tall: false },
  { src: "/images/lieux/interieur-77.webp", fr: "La salle à manger", en: "The dining room", tall: false },
  { src: "/images/lieux/interieur-79.webp", fr: "La cuisine du Prieuré", en: "The Priory kitchen", tall: true },
  { src: "/images/lieux/interieur-72.webp", fr: "Le palier au lustre de Murano", en: "The Murano chandelier landing", tall: true },
  { src: "/images/lieux/interieur-89.webp", fr: "La chapelle en restauration", en: "The chapel under restoration", tall: true },
  { src: "/images/lieux/interieur-61.webp", fr: "Le vitrail aux losanges", en: "The diamond stained-glass window", tall: true },
  { src: "/images/lieux/interieur-75.webp", fr: "Le hall et l'escalier de pierre", en: "The hall and stone staircase", tall: true },
  { src: "/images/lieux/interieur-60.webp", fr: "Une chambre du Prieuré", en: "A Priory bedroom", tall: false },
  { src: "/images/lieux/interieur-87.webp", fr: "PRÉSENCE — l'ébauche d'argile d'Auriane de Potesta", en: "PRÉSENCE — the clay sketch by Auriane de Potesta", tall: true },
  { src: "/images/lieux/interieur-88.webp", fr: "La fresque en création dans la chapelle", en: "The fresco in creation in the chapel", tall: false },
  { src: "https://images.pexels.com/photos/35298543/pexels-photo-35298543.jpeg?auto=compress&cs=tinysrgb&w=1200", fr: "Reflet du Mont", en: "Reflection of the Mont", tall: true },
];

const Gallery = () => {
  const { lang, t } = useLang();
  const [active, setActive] = useState(null);

  return (
    <section id="galerie" data-testid="gallery-section" className="mx-auto max-w-[1600px] px-4 py-24 sm:px-8 lg:px-16 lg:py-36">
      <Reveal>
        <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-gold">{t.gallery.kicker}</p>
      </Reveal>
      <Reveal delay={0.1}>
        <h2 className="mt-4 font-serif text-3xl font-light text-parchment sm:text-4xl lg:text-5xl">
          {t.gallery.title}
        </h2>
      </Reveal>

      <div className="mt-14 columns-1 gap-5 sm:columns-2 lg:columns-3">
        {IMAGES.map((img, i) => (
          <Reveal key={img.src} delay={(i % 3) * 0.08} className="mb-5 break-inside-avoid">
            <button
              data-testid="gallery-image-item"
              onClick={() => setActive(img)}
              className="group relative block w-full overflow-hidden border border-gold/10"
            >
              <img
                src={img.src}
                alt={lang === "fr" ? img.fr : img.en}
                loading="lazy"
                className={`w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 ${img.tall ? "aspect-[3/4]" : "aspect-[4/3]"}`}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
              <span className="absolute bottom-4 left-4 font-mono text-[10px] uppercase tracking-[0.25em] text-parchment opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                {lang === "fr" ? img.fr : img.en}
              </span>
            </button>
          </Reveal>
        ))}
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setActive(null)}
            className="fixed inset-0 z-50 flex items-center justify-center bg-ink/92 p-4 backdrop-blur-lg sm:p-10"
          >
            <button
              data-testid="gallery-lightbox-close"
              aria-label={t.gallery.close}
              onClick={() => setActive(null)}
              className="absolute right-5 top-5 rounded-full border border-gold/40 bg-ink/70 p-2.5 text-parchment transition-colors hover:bg-gold hover:text-ink"
            >
              <X size={20} />
            </button>
            <motion.img
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              src={active.src}
              alt={lang === "fr" ? active.fr : active.en}
              className="max-h-full max-w-full border border-gold/25 object-contain"
            />
            <p className="absolute bottom-6 font-mono text-[10px] uppercase tracking-[0.3em] text-sand">
              {lang === "fr" ? active.fr : active.en}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Gallery;
